import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import cn from 'classnames';
import { createStructuredSelector } from 'reselect';
import { thunkToAction } from 'typescript-fsa-redux-thunk';

import { EOwnerType } from '@enums';

import { Translate } from '@decorators';

import {
	dmcaFilesSelector,
	dmcaFormSelector,
	addFiles as addFilesAction,
	removeFile as removeFileAction,
	submitDmca as submitDmcaAction,
	uploadFile as uploadFileAction,
} from '@redux';

import { Feedback } from '@common/components/Feedback';
import { Icon } from '@common/components/Icon';
import { MultilineText } from '@common/components/MultilineText';
import { ContainedButton, FormRow, RadioButton, Spinner } from '@common/components/ui';
import { AppStat } from '@common/utils/AppStat';

import { IconCheck } from '@icons';

import { EDmcaFormObjects } from '../../enums';
import { DmcaCase } from '../DmcaCase';
import { DmcaConfirmations } from '../DmcaConfirmations';
import { DmcaIndividual } from '../DmcaIndividual';
import { DmcaLegalEntity } from '../DmcaLegalEntity';
import { DmcaSuccess } from '../DmcaSuccess';

import { IFile } from '@interfaces';

import { IDmcaFormProps, IDmcaFormState, TDmcaFormObject } from './types';

import * as s from './DmcaForm.scss';

@Translate('common.containers.Dmca.components.DmcaForm')
export class DmcaFormContainer extends React.Component<IDmcaFormProps, IDmcaFormState> {

	state: IDmcaFormState = {
		isOwnerIndividual: true,
		dmcaCase: {
			description: null,
			confirmation: null,
			files: [],
		},
		individual: {
			fullName: null,
			passportSeries: null,
			passportNumber: null,
			passportIssueDate: null,
			passportIssuePlace: null,
			phone: null,
			email: null,
		},
		legalEntity: {
			title: null,
			ogrn: null,
			legalAddress: null,
			realAddress: null,
			phone: null,
			email: null,
			contactPersonFullName: null,
			contactPersonPosition: null,
		},
		confirmations: {
			isOwnershipConfirmed: null,
			isCopyrightInfringementConfirmed: null,
			isValidityOfClaimsConfirmed: null,
			isCommunicationByEmailConfirmed: null,
			isPersonalDataUseConfirmed: null,
		},

		errorFields: {
			dmcaCase: [],
			individual: [],
			legalEntity: [],
			confirmations: [],
		},
	};

	isValid = false;

	componentDidUpdate(prevProps: Readonly<IDmcaFormProps>, prevState: Readonly<IDmcaFormState>) {
		const {
			form: {
				meta: {
					error,
				},
			},
		} = this.props;
		const {
			form: {
				meta: {
					error: prevError,
				},
			},
		} = prevProps;
		if (!prevError && error) {
			AppStat.log.boom.showErrorForBoomDmca();
		}
	}

	validateObject(name: EDmcaFormObjects) {
		const { files } = this.props;

		let errorFields = Object.entries(this.state[name])
			.filter(([_, value]) => !value)
			.map(([key]) => key);

		if (errorFields.includes('confirmation') && files.length) {
			errorFields = errorFields.filter((field) => field !== 'confirmation');
		}

		if (errorFields.length) {
			this.setState((state) => ({
				...state,
				errorFields: {
					...state.errorFields,
					[name]: errorFields,
				},
			}));

			this.isValid = false;
		}
	}

	clearDmcaCaseConfirmationError() {
		this.setState((state) => ({
			...state,
			errorFields: {
				...state.errorFields,
				[EDmcaFormObjects.DMCA_CASE]: state.errorFields[EDmcaFormObjects.DMCA_CASE].filter(
					(field) => field !== 'confirmation' && field !== 'files',
				),
			},
		}));
	}

	validateFiles() {
		const { files } = this.props;
		const filesInProcess = files.filter((file) => !file.meta.isFetched);

		if (filesInProcess.length) {
			this.setState((state) => ({
				...state,
				errorFields: {
					...state.errorFields,
					[EDmcaFormObjects.DMCA_CASE]: [...state.errorFields[EDmcaFormObjects.DMCA_CASE], 'files'],
				},
			}));

			this.isValid = false;
		}
	}

	validate() {
		const { isOwnerIndividual } = this.state;

		this.isValid = true;

		isOwnerIndividual
			? this.validateObject(EDmcaFormObjects.INDIVIDUAL)
			: this.validateObject(EDmcaFormObjects.LEGAL_ENTITY);
		this.validateObject(EDmcaFormObjects.DMCA_CASE);
		this.validateObject(EDmcaFormObjects.CONFIRMATIONS);
		this.validateFiles();
	}

	onObjectChange = (name: EDmcaFormObjects) => (changes: Partial<TDmcaFormObject>) => {
		const changedFields = Object.keys(changes);

		this.setState((state) => ({
			...state,
			[name]: {
				...state[name],
				...changes,
			},
			errorFields: {
				...state.errorFields,
				[name]: state.errorFields[name].filter((fieldName) => !changedFields.includes(fieldName)),
			},
		}));
	};

	onDmcaCaseChange = this.onObjectChange(EDmcaFormObjects.DMCA_CASE);

	onIndividualChange = this.onObjectChange(EDmcaFormObjects.INDIVIDUAL);

	onLegalEntityChange = this.onObjectChange(EDmcaFormObjects.LEGAL_ENTITY);

	onConfirmationChange = this.onObjectChange(EDmcaFormObjects.CONFIRMATIONS);

	onOwnerTypeChange = (ownerType: EOwnerType) => {
		this.setState({
			isOwnerIndividual: ownerType === EOwnerType.INDIVIDUAL,
		});
	};

	onFileRemove = (file: IFile) => {
		const {
			actions: {
				removeFile,
			},
		} = this.props;

		this.clearDmcaCaseConfirmationError();

		removeFile({ file });
	};

	onFileUpload = (file: IFile) => {
		const {
			actions: {
				uploadFile,
			},
		} = this.props;

		this.clearDmcaCaseConfirmationError();

		void uploadFile({ file });
	};

	onFileUploadCancel = () => {
		AppStat.log.boom.uploadFileCancelledForBoomDmca();
	};

	onFileUploadError = (error: Error) => {
		AppStat.log.boom.uploadFileErrorForBoomDmca(error);
	};

	onFileUploadSuccess = () => {
		AppStat.log.boom.uploadFileSuccessForBoomDmca();
	};

	onFilesAdd = (files: IFile[]) => {
		const {
			actions: {
				addFiles,
			},
		} = this.props;

		this.clearDmcaCaseConfirmationError();

		addFiles({ files });
	};

	onSubmit = () => {
		const {
			files: filesUploading,
			actions: {
				submitDmca,
			},
		} = this.props;
		const {
			isOwnerIndividual,
			dmcaCase: {
				description,
				confirmation,
			},
			individual,
			legalEntity,
		} = this.state;

		this.validate();

		if (this.isValid) {
			const files = filesUploading.map(({ data: { id } }) => id);
			const owner = isOwnerIndividual ? individual : legalEntity;

			void submitDmca({
				description,
				confirmation,
				isOwnerIndividual,
				files,
				owner,
			});
		}
	};

	render() {
		const {
			form: {
				meta: {
					isFetching: isFormSubmitting,
					isFetched: isFormSubmitted,
					error: submitError,
				},
			},
			files,
		} = this.props;
		const {
			isOwnerIndividual,
			dmcaCase,
			individual,
			legalEntity,
			confirmations,
			errorFields,
		} = this.state;

		const isVisible = {
			individual: isOwnerIndividual,
			legalEntity: !isOwnerIndividual,
			submitError: !!submitError,
		};

		if (isFormSubmitting) {
			return (
				<div className={s.spinnerContainer}>
					<Spinner
						size={80}
						color="#d5d5d5"
					/>
				</div>
			);
		}

		if (isFormSubmitted) {
			return (
				<DmcaSuccess/>
			);
		}

		return (
			<div className={cn(s.block)}>
				<div className={s.prompt}>
					<MultilineText text={this.t('prompt')}/>
					{' '}
					<Feedback
						title={this.t('support')}
						classNames={{
							block: s.feedbackBlock,
							link: s.feedbackLink,
						}}
					/>
					{'.'}
				</div>

				<div className={s.title}>
					<MultilineText text={this.t('title')} />
				</div>

				<DmcaCase
					{...dmcaCase}
					files={files}
					errorFields={errorFields.dmcaCase}
					onChange={this.onDmcaCaseChange}
					onFilesAdd={this.onFilesAdd}
					onFileRemove={this.onFileRemove}
					onFileUpload={this.onFileUpload}
					onFileUploadCancel={this.onFileUploadCancel}
					onFileUploadError={this.onFileUploadError}
					onFileUploadSuccess={this.onFileUploadSuccess}
				/>

				<div className={s.title}>
					<MultilineText text={this.t('ownerInfo')} />
				</div>

				<FormRow className={s.formRow}>
					<RadioButton
						classNameContainer={s.radio}
						name="ownerType"
						isDefaultChecked={isOwnerIndividual}
						value={EOwnerType.INDIVIDUAL}
						label={this.t('individual')}
						onRadioButtonClick={this.onOwnerTypeChange}
					/>

					<RadioButton
						classNameContainer={s.radio}
						name="ownerType"
						isDefaultChecked={!isOwnerIndividual}
						value={EOwnerType.LEGAL_ENTITY}
						label={this.t('legalEntity')}
						onRadioButtonClick={this.onOwnerTypeChange}
					/>
				</FormRow>

				{isVisible.individual &&
					<DmcaIndividual
						{...individual}
						errorFields={errorFields.individual}
						onChange={this.onIndividualChange}
					/>
				}

				{isVisible.legalEntity &&
					<DmcaLegalEntity
						{...legalEntity}
						errorFields={errorFields.legalEntity}
						onChange={this.onLegalEntityChange}
					/>
				}

				<DmcaConfirmations
					{...confirmations}
					errorFields={errorFields.confirmations}
					onChange={this.onConfirmationChange}
				/>

				<ContainedButton className={s.sendButton} onClick={this.onSubmit} withIcon={true}>
					<Icon glyph={IconCheck} className={s.checkIcon}/>
					{this.t('send')}
				</ContainedButton>

				{isVisible.submitError &&
					<div className={s.submitError}>
						{this.t('submitError')}
					</div>
				}
			</div>
		);
	}

}

const mapStateToProps = createStructuredSelector({
	files: dmcaFilesSelector,
	form: dmcaFormSelector,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	actions: bindActionCreators(
		{
			addFiles: addFilesAction,
			uploadFile: thunkToAction(uploadFileAction.action),
			removeFile: removeFileAction,
			submitDmca: thunkToAction(submitDmcaAction.action),
		},
		dispatch,
	),
});

export const DmcaForm = connect(mapStateToProps, mapDispatchToProps)(DmcaFormContainer);
